import React, { useRef, Suspense } from "react";
import {
  UseCanvas,
  useScrollRig,
  useImageAsTexture,
  styles,
} from "@14islands/r3f-scroll-rig";
import { ParallaxScrollScene } from "@14islands/r3f-scroll-rig/powerups";
import { Image as DreiImage, Circle } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { clamp } from "three/src/math/MathUtils";
import { DoubleSide } from "three";

export function Image({ src, parallaxSpeed = 1, ...props }) {
  const el = useRef();
  const img = useRef();
  const { hasSmoothScrollbar } = useScrollRig();
  return (
    <>
      <div ref={el} {...props}>
        <img
          className={styles.hiddenWhenSmooth}
          ref={img}
          src={src}
          loading="eager"
          decode="async"
          alt="Hold on, hold on, I am being loaded..."
        />
      </div>

      {hasSmoothScrollbar && (
        <UseCanvas debug={false}>
          <ParallaxScrollScene track={el} speed={parallaxSpeed}>
            {(props) => (
              <Suspense fallback={<LoadingIndicator {...props} />}>
                <WebGLImage imgRef={img} {...props} />
              </Suspense>
            )}
          </ParallaxScrollScene>
        </UseCanvas>
      )}
    </>
  );
}

function WebGLImage({ imgRef, scrollState, dir, ...props }) {
  const ref = useRef();

  const texture = useImageAsTexture(imgRef);

  useFrame(({ clock }) => {
    ref.current.material.grayscale = clamp(
      1 - scrollState.visibility ** 1,
      0,
      1
    );
    ref.current.material.opacity = clamp(scrollState.viewport * 2, 0, 1);
  });
  return <DreiImage ref={ref} texture={texture} transparent {...props} />;
}

function LoadingIndicator({ scale }) {
  const box = useRef();
  useFrame(({ clock }) => {
    box.current.rotation.y = clock.getElapsedTime() * 5;
  });
  return (
    <group scale={scale.xy.times(0.05).min()}>
      <Circle ref={box}>
        <meshNormalMaterial side={DoubleSide} />
      </Circle>
      <Circle>
        <meshNormalMaterial side={DoubleSide} />
      </Circle>
    </group>
  );
}
